/* =====================================================================
   assets/soi-sticky.js · Spirit of India · M1: sticky ATC visibility and mirroring (02 §5, D6; 04 §9; 05 C31)
   <soi-sticky-atc data-for="<buybox id>"> mirrors the buy box byte for byte (label line 1 is copied from the
   real ATC) and adds the current selection with the same request (`_soi_source` = `<source>:sticky`, 04 §9.3).
   Visibility (04 §9.1, D6): at every width the bar shows whenever the real ATC is not ≥ 50 % in view (below the
   fold or scrolled past), computed from its rect on scroll / resize, so anchor jumps and scroll restore count. Always hidden while a dialog, the cart drawer or the footer legend is visible.
   While visible, <body> gets --sticky-offset (the bar's measured height) as padding so content and toasts are never covered.
   ===================================================================== */
(() => {
  'use strict';
  if (customElements.get('soi-sticky-atc')) return;
  const mqS = window.matchMedia('(max-width: 767.98px)');
  const mqM = window.matchMedia('(min-width: 768px) and (max-width: 989.98px)');

  class SoiStickyAtc extends HTMLElement {
    connectedCallback() {
      this.box = document.getElementById(this.dataset.for);
      this.btn = this.querySelector('[data-sticky-atc]');
      this.line1 = this.querySelector('[data-sticky-line1]');
      this.infoL = this.querySelector('[data-sticky-info]');
      this.infoS = this.querySelector('[data-sticky-info-text]');
      if (!this.box || !this.btn) { this.hidden = true; return; }
      this.realAtc = this.box.querySelector('[data-soi="atc"]');
      this.atcVisible = false;
      this.atcAbove = false;
      this.legendVisible = false;
      this.busy = false;

      this.box.addEventListener('soi:buybox:change', (e) => this.mirror(e.detail));
      // set boxes send no info line: on S the server line loses the „Ritual-Set" prefix and the ml total (the buy
      // box shows both), so „5 Roll-ons · 98,00 €/100 ml · 30 Tage Garantie‡" stays on one line (R2-A9-07)
      this.setInfo = this.infoS ? this.infoS.textContent.trim() : '';
      this.trimSetInfo();
      mqS.addEventListener('change', () => this.trimSetInfo());
      mqM.addEventListener('change', () => this.last && this.mirror(this.last));
      this.box.addEventListener('soi:buybox:busy', (e) => { this.busy = !!(e.detail && e.detail.busy); });
      if (this.box.summary) this.mirror(this.box.summary);
      this.btn.addEventListener('click', () => {
        if (typeof this.box.add === 'function') this.box.add('sticky', this.btn);
      });
      this.querySelectorAll('[data-sticky-scroll]').forEach((b) => b.addEventListener('click', () => {
        const anchor = document.getElementById('ritual') || this.box;
        anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const checked = this.box.querySelector('.soi-tier__radio:checked') || this.realAtc;
        if (checked) setTimeout(() => checked.focus({ preventScroll: true }), 400);
      }));

      // Visibility is computed from the real ATC's rect (passive scroll + resize, rAF-throttled, once at connect),
      // so jumps past an ATC that never intersected (anchors, scroll restore, wheel steps) are caught too.
      this.headerH = () => parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-h'), 10) || 64;
      this.measure = () => {
        this.raf = 0;
        if (!this.realAtc || !this.realAtc.isConnected) return;
        const r = this.realAtc.getBoundingClientRect();
        const top = this.headerH();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        const visH = Math.max(0, Math.min(r.bottom, vh) - Math.max(r.top, top));
        this.atcVisible = r.height > 0 && visH / r.height >= 0.5;
        this.atcAbove = r.bottom <= top + 1;
        this.update();
      };
      this.onScroll = () => { if (!this.raf) this.raf = requestAnimationFrame(this.measure); };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      const legend = document.getElementById('soi-footnotes');
      if (legend && 'IntersectionObserver' in window) {
        this.io2 = new IntersectionObserver(([en]) => { this.legendVisible = en.isIntersecting; this.update(); });
        this.io2.observe(legend);
      }
      // body padding follows the bar's real height (04 §9 #6), never a token guess
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.syncOffset());
        this.ro.observe(this);
      }
      // layers above the bar (04 §0.2 #8): any open dialog, the cart drawer and the cookie banner. Mutations are
      // batched into one frame; a closing dialog re-measures in the next frame, so the bar returns without a scroll
      // (R3-A9-02); the banner is injected late and answered by its own buttons (R3-A9-03, R3-A5-03)
      this.queue = () => { if (!this.uraf) this.uraf = requestAnimationFrame(() => { this.uraf = 0; this.update(); }); };
      this.mo = new MutationObserver(this.queue);
      this.mo.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['open', 'class', 'style', 'hidden', 'data-soi-layer-open'] });
      document.addEventListener('close', () => requestAnimationFrame(() => this.measure()), true);
      document.addEventListener('soi:dialog-open', () => this.update(), true);
      document.addEventListener('visitorConsentCollected', () => requestAnimationFrame(() => this.measure()));
      document.addEventListener('click', (e) => {
        if (e.target.closest && e.target.closest('#shopify-pc__banner, #shopify-pc__prefs, [id^="shopify-pc__"]')) setTimeout(() => this.measure(), 60);
      }, true);
      [1000, 2500].forEach((ms) => setTimeout(() => this.update(), ms));
      // S: a focused text field (newsletter, search) is never covered by the bar (04 §9.1, R3-A9-01)
      this.inputFocus = false;
      const textField = (el) => !!(el && el.matches && el.matches('input:not([type=checkbox]):not([type=radio]):not([type=button]):not([type=submit]):not([type=range]):not([type=hidden]), textarea, select'));
      document.addEventListener('focusin', (e) => {
        if (mqS.matches && textField(e.target) && !this.contains(e.target)) { this.inputFocus = true; this.update(); }
      });
      document.addEventListener('focusout', () => {
        if (!this.inputFocus) return;
        requestAnimationFrame(() => { if (!textField(document.activeElement)) { this.inputFocus = false; this.update(); } });
      });
      mqS.addEventListener('change', () => this.onScroll());
      this.measure();
      if (document.readyState !== 'complete') window.addEventListener('load', () => this.measure(), { once: true });
    }
    disconnectedCallback() {
      [this.io2, this.ro, this.mo].forEach((o) => o && o.disconnect());
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }
    syncOffset() {
      const h = this.offsetHeight;
      if (h) document.body.style.setProperty('--soi-sticky-bar-h', `${h}px`);
    }
    layerOpen() {
      // open layers only (a stale header attribute can never hold the bar back, R3-A9-02)
      if (document.querySelector('dialog[open], cart-drawer.active, cart-drawer[open], .drawer.active')) return true;
      return this.bannerOpen();
    }
    /** the Shopify privacy banner or its preferences dialog is on screen (04 §0.2 #8, 03 D18) */
    bannerOpen() {
      const els = document.querySelectorAll('#shopify-pc__banner, #shopify-pc__prefs');
      for (const b of els) {
        const cs = getComputedStyle(b);
        if (cs.display !== 'none' && cs.visibility !== 'hidden' && Number(cs.opacity) > 0.05 && b.getBoundingClientRect().height > 0) return true;
      }
      return false;
    }
    trimSetInfo() {
      if (!this.infoS || !this.setInfo || (this.box.summary && this.box.summary.infoS)) return;
      const segs = this.setInfo.split(' · ');
      let out = segs;
      if (mqS.matches && segs.length > 2) out = segs.slice(1).filter((x) => !/^\d+(?:[,.]\d+)?\s?ml$/.test(x.trim()));
      this.infoS.textContent = out.join(' · ');
    }
    mirror(s) {
      if (!s) return;
      this.last = s;
      if (s.line1 && this.line1 && this.line1.innerHTML !== s.line1) this.line1.innerHTML = s.line1;
      if (s.infoL && this.infoL) this.infoL.textContent = (mqM.matches && s.infoM) || s.infoL;
      if (s.infoS && this.infoS) this.infoS.textContent = s.infoS;
      if (this.btn) { this.btn.disabled = !!s.disabled; this.btn.toggleAttribute('aria-disabled', !!s.disabled); }
    }
    update() {
      // 04 §9.1: shown whenever the real ATC is not (at least half) in view, at every width
      let show = !this.atcVisible;
      if (this.legendVisible || this.inputFocus || this.layerOpen()) show = false;
      const pad = show || !this.atcVisible;
      if (show === this.shown && pad === this.padded) return;
      this.padded = pad;
      this.shown = show;
      this.classList.toggle('is-visible', show);
      this.inert = !show;
      this.setAttribute('aria-hidden', show ? 'false' : 'true');
      if (show) this.syncOffset();
      // the body keeps its bottom padding while the bar is only held back by the footer legend or a layer above it,
      // so the document never gets shorter at the footer (no scroll jump, no flicker at the threshold: R2-A9-13)
      document.body.classList.toggle('soi-has-sticky', show || !this.atcVisible);
    }
  }
  customElements.define('soi-sticky-atc', SoiStickyAtc);
})();
